import React, { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const CurrencyContext = createContext();

// Hardcoded fallback rates (used if API fails)
const fallbackRates = {
    "USD": 1,
    "EGP": 50,
    "SDG": 600
};


export const CurrencyProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [currency, setCurrency] = useState(localStorage.getItem("currency") || "USD");

    // State for live currency rates
    const [currencyRates, setCurrencyRates] = useState(() => {
        const cached = localStorage.getItem("currencyRates");
        const timestamp = localStorage.getItem("currencyRatesTimestamp");

        if (cached && timestamp) {
            const age = Date.now() - parseInt(timestamp);
            const ONE_DAY = 24 * 60 * 60 * 1000;
            if (age < ONE_DAY) {
                try {
                    return JSON.parse(cached);
                } catch (e) {
                    console.warn("Failed to parse cached currency rates:", e);
                }
            }
        }
        return fallbackRates;
    });

    const [ratesLoading, setRatesLoading] = useState(false);

    useEffect(() => {
        const fetchCurrencyRates = async () => {
            const timestamp = localStorage.getItem("currencyRatesTimestamp");
            if (timestamp) {
                const age = Date.now() - parseInt(timestamp);
                const ONE_DAY = 24 * 60 * 60 * 1000;
                if (age < ONE_DAY) return;
            }

            setRatesLoading(true);
            try {
                const response = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json');
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                const newRates = {
                    USD: 1,
                    EGP: data.usd?.egp || fallbackRates.EGP,
                    SDG: data.usd?.sdg || fallbackRates.SDG
                };
                setCurrencyRates(newRates);
                localStorage.setItem("currencyRates", JSON.stringify(newRates));
                localStorage.setItem("currencyRatesTimestamp", Date.now().toString());
            } catch (error) {
                console.warn("⚠️ Failed to fetch currency rates:", error.message);
            } finally {
                setRatesLoading(false);
            }
        };
        fetchCurrencyRates();
    }, []);

    useEffect(() => {
        localStorage.setItem("currency", currency);
    }, [currency]);

    const exchangeRate = currencyRates[currency] || 1;

    const getCurrencySymbol = (curr) => {
        switch (curr) {
            case "USD": return "$";
            case "EGP": return "EGP";
            case "SDG": return "SDG";
            default: return "$";
        }
    };

    const getPrice = (product) => {
        if (!product) return 0;
        switch (currency) {
            case "EGP": return product.price_egp || product.price || 0;
            case "SDG": return product.price_sdg || product.price || 0;
            default: return product.price || 0;
        }
    };

    const formatPrice = (product) => {
        const price = getPrice(product);
        const currentLang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
        return new Intl.NumberFormat(currentLang, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'code',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(price);
    };

    return (
        <CurrencyContext.Provider value={{
            currency,
            setCurrency,
            getCurrencySymbol,
            getPrice,
            formatPrice,
            exchangeRate,
            currencyRates,
            ratesLoading
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};


export const useCurrency = () => useContext(CurrencyContext);
